import {
  Build,
  Component,
  Host,
  Listen,
  Method,
  State,
  h,
} from '@stencil/core';
import { Unsubscribe, store } from '@stencil/redux';
import {
  ContinueResponse,
  PollQrResponse,
  QrChallenge,
} from '../../class/class';
import {
  Constants,
  Language,
  NotificationType,
  QrStatus,
} from '../../enums/const-enums';
import { uiReady } from '../../redux/actions/config-actions';
import { setActiveMethodStatus } from '../../redux/actions/method-action';
import {
  clearNotification,
  showNotification,
} from '../../redux/actions/notification-action';
import { RootState, initialStore } from '../../redux/store';
import { GetChallenge } from '../../service/get-challenge';
import { SignPollQr } from '../../service/sign-pollqr';
import { isDarkMode } from '../../utils/theming';
import {
  getBaseUrl,
  handleContinueResponse,
  handleErrorResponse,
  logd,
} from '../../utils/utils';
import { RassQrTranslations, rassQrTranslations } from './i18n/rass-qr.i18n';

@Component({
  tag: 'rass-qr',
  styleUrl: 'rass-qr.scss',
  shadow: false,
})
export class RassQr {
  @State() assetsPath: string;
  @State() language: Language;
  @State() challengeData: QrChallenge;
  @State() qrStatus: QrStatus;
  @State() pollCount: number;
  pollCountTimeOut = Build.isTesting || Build.isDev ? 3 : 15;
  pollInterval: ReturnType<typeof setTimeout>;
  @State() isChallengeRequestPending = false;
  isPollRequestPending = false;
  baseUrl = getBaseUrl();
  clearNotification!: typeof clearNotification;
  showNotification!: typeof showNotification;
  setActiveMethodStatus!: typeof setActiveMethodStatus;
  uiReady!: typeof uiReady;
  unsubscribe!: Unsubscribe;

  async componentWillLoad(): Promise<void> {
    store.setStore(initialStore);
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language },
        challengeData,
      } = state;
      return { assetsPath, language, challengeData };
    });
    store.mapDispatchToProps(this, {
      showNotification,
      clearNotification,
      setActiveMethodStatus,
      uiReady,
    });
    this.clearNotification();
    if (this.challengeData.qr_id !== undefined) {
      await this.startPolling();
    } else {
      this.getChallenge();
    }
  }

  componentDidLoad(): void {
    window.scrollTo(0, 0);
    (document.activeElement as HTMLElement).blur();
    this.uiReady();
  }

  disconnectedCallback(): void {
    this.stopPolling();
    this.qrStatus = undefined;
  }

  getChallenge(): void {
    this.isChallengeRequestPending = true;
    new GetChallenge()
      .doGetChallenge()
      .then(async () => {
        this.isChallengeRequestPending = false;
        await this.startPolling();
      })
      .catch((error: AppModel.HttpResponseErrorJson) => {
        handleErrorResponse(error, this.translations.errors.timeout);
        this.qrStatus = QrStatus.ERROR;
        this.isChallengeRequestPending = false;
      });
  }

  @Listen('keydown', {
    target: 'document',
  })
  onKeydown(event: KeyboardEvent): void {
    if (this.isRefreshable && event.key === 'Enter') {
      this.getChallenge();
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  @Method()
  async startPolling(): Promise<void> {
    logd(() => console.log('Start polling...'));
    this.stopPolling();
    this.clearNotification();
    this.pollCount = -1;
    this.doPollQr();
    this.pollInterval = setInterval(
      () => this.doPollQr(),
      Constants.DEFAULT_POLL_TIME,
    );
  }

  stopPolling(): void {
    if (this.pollInterval) {
      logd(() => console.log('Stop polling....'));
      clearInterval(this.pollInterval);
    }
    this.isPollRequestPending = false;
  }

  doPollQr(): void {
    if (this.challengeData.qr_id && !this.isPollRequestPending) {
      this.isPollRequestPending = true;
      new SignPollQr()
        .doPollQr(this.challengeData.qr_id)
        .then((resp: PollQrResponse) => {
          if (resp.qr_status === QrStatus.CONFIRMED) {
            this.stopPolling();
            const event = new CustomEvent('rass:sign-submit:success');
            window.dispatchEvent(event);
            handleContinueResponse(new ContinueResponse(resp), 'success');
          } else {
            this.updateQrStatus(resp.qr_status);
          }
        })
        .catch((error: AppModel.HttpResponseErrorJson) => {
          handleErrorResponse(error, this.translations.errors.timeout);
          this.updateQrStatus(QrStatus.ERROR);
        });
    }
  }

  updateQrStatus(qrStatus: QrStatus): void {
    this.isPollRequestPending = false;
    this.pollCount++;
    if (
      qrStatus === QrStatus.ISSUED &&
      this.pollCount == this.pollCountTimeOut
    ) {
      this.qrStatus = QrStatus.TIMEOUT;
      logd(() =>
        console.warn(
          `Polling is stopping after next poll: ${this.pollCount}/${this.pollCountTimeOut}`,
        ),
      );
    } else if (
      qrStatus === QrStatus.ISSUED &&
      this.pollCount > this.pollCountTimeOut
    ) {
      this.stopPolling();
      logd(() =>
        console.warn(
          `Polling is stopped: ${this.pollCount}/${this.pollCountTimeOut}`,
        ),
      );
      return;
    } else {
      this.qrStatus = qrStatus;
    }
    logd(() =>
      console.log(
        `Polling ${this.qrStatus}: ${this.pollCount}/${this.pollCountTimeOut}`,
      ),
    );
    switch (this.qrStatus) {
      case QrStatus.ISSUED:
      case QrStatus.SCANNED:
        this.clearNotification();
        break;
      case QrStatus.CANCELED:
        this.stopPolling();
        this.handleNotification(
          '',
          this.translations.notifications[this.qrStatus].text,
          this.translations.notifications[this.qrStatus].type,
        );
      case QrStatus.TIMEOUT:
        break;
      case QrStatus.BLOCKED:
      case QrStatus.REJECTED:
        this.handleNotification(
          '',
          this.translations.notifications[this.qrStatus].text,
          this.translations.notifications[this.qrStatus].type,
        );
      case QrStatus.ERROR:
      default:
        this.stopPolling();
    }
    this.setActiveMethodStatus(this.qrStatus);
  }

  handleNotification(
    code: string,
    text: string,
    type: string = NotificationType.WARNING,
  ): void {
    this.showNotification({
      type: type,
      header: '',
      body: text + (code ? ` (${code})` : ''),
    });
  }

  get isPollingPaused(): boolean {
    return (
      this.qrStatus === QrStatus.BLOCKED ||
      this.qrStatus === QrStatus.CANCELED ||
      this.qrStatus === QrStatus.TIMEOUT
    );
  }

  get isRefreshable(): boolean {
    return this.isPollingPaused && !this.isChallengeRequestPending;
  }

  get challengeImage(): string {
    switch (this.qrStatus) {
      case QrStatus.ISSUED:
        if (this.challengeData.qr_id) {
          return `${this.baseUrl}get_qr_image/qslqr.png?id=${this.challengeData.qr_id}&count=${this.pollCount}&version=2`;
        }
        return `${this.assetsPath}images/qr-${this.imageThemeFragment}-error.png`;
      case QrStatus.SCANNED:
        return `${this.assetsPath}images/qr-${this.imageThemeFragment}-scanned.png`;
      case QrStatus.CANCELED:
      case QrStatus.TIMEOUT:
        return `${this.assetsPath}images/qr-${this.imageThemeFragment}-timeout.png`;
      case QrStatus.BLOCKED:
      case QrStatus.REJECTED:
      case QrStatus.ERROR:
      default:
        return `${this.assetsPath}images/qr-${this.imageThemeFragment}-error.png`;
    }
  }

  get imageThemeFragment(): string {
    return isDarkMode() ? 'dm' : 'lm';
  }

  get challengeInstructions(): string {
    if (this.challengeData.is_qr_high_signing) {
      return this.translations.QR_HIGH_RISK_SIGNING;
    }
    return this.translations.instructions[this.qrStatus];
  }

  get translations(): RassQrTranslations {
    return rassQrTranslations[this.language] || rassQrTranslations[Language.EN];
  }

  render(): void {
    return (
      <Host class="section-qr">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="sfc-container body-width" role="form">
          <div class="sfc-row sfc-justify-content-center sfc-margin-block-end-150">
            <div class={this.qrStatus ? 'sfc-display-none' : 'qr-spinner'}>
              <sfc-spinner size="md" />
            </div>
            <div class={this.qrStatus ? 'challenge-image' : 'sfc-display-none'}>
              <img
                id="challenge-image"
                class={`challenge-image-qr ${this.isPollingPaused ? ' paused' : ''}`}
                src={this.challengeImage}
                onClick={
                  this.isRefreshable ? (): void => this.getChallenge() : null
                }
                alt="QR-code"
              />
            </div>
          </div>

          <div class="sfc-row sfc-justify-content-center sfc-padding-block-end-200">
            <div class="sfc-col-12">
              <p
                id="challenge-instructions"
                aria-live="polite"
                class={`${QrStatus.TIMEOUT === this.qrStatus ? 'sfc-margin-block-start-0 sfc-margin-block-end-0' : 'sfc-paragraph sfc-text--size-md sfc-font-text-align-center'}`}
                innerHTML={this.challengeInstructions}
              />
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
